<script setup lang="ts">
// TaskRemoveConfirm — v3.19.31 任务删除统一确认弹窗。
//
// 删除现在是【全节点同步】语义 (所有节点上的同任务副本一并清除) + 可选
// 同时删除已下载文件, 且键盘 Delete 也能触发删除 — 必须先确认。
// 组件挂载于 TasksView / TaskDetailView, 所有删除入口共用一个实例
// (确认状态在 useTaskActions 模块级共享)。
//
// v3.19.32 fix: ui/Dialog 自带底部按钮并对外发 confirm/cancel 事件,
// 不渲染父级 #footer 插槽 — 此前把按钮塞进 #footer 又没监听 @confirm,
// 点击"确认"无任何动作, 弹窗永远关不掉 (完成任务删不掉的直接原因)。
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import Dialog from '@/components/ui/Dialog.vue'
import { useTaskActions } from '@/composables/useTaskActions'

const { t } = useI18n()
const { removeConfirmState, confirmRemove, cancelRemove } = useTaskActions()

const deleteFiles = ref(false)

const show = computed({
  get: () => !!removeConfirmState.value,
  set: (v: boolean) => {
    if (!v) onCancel()
  },
})

const name = computed(() => removeConfirmState.value?.name ?? '')

function onConfirm(): void {
  const files = deleteFiles.value
  deleteFiles.value = false
  void confirmRemove(files)
}

function onCancel(): void {
  deleteFiles.value = false
  cancelRemove()
}
</script>

<template>
  <Dialog
    v-model="show"
    :title="t('tasks.confirmRemoveTitle')"
    variant="danger"
    :confirm-text="t('common.delete')"
    @confirm="onConfirm"
    @cancel="onCancel"
  >
    <div class="space-y-3 text-left">
      <p class="text-sm text-fg">{{ t('tasks.confirmRemoveBody', { name }) }}</p>
      <p class="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-fg-muted">
        {{ t('tasks.confirmRemoveClusterHint') }}
      </p>
      <label class="flex cursor-pointer items-center gap-2 text-sm text-fg">
        <input
          v-model="deleteFiles"
          type="checkbox"
          class="h-4 w-4 rounded border-border accent-red-600"
        />
        {{ t('tasks.confirmRemoveFiles') }}
      </label>
    </div>
  </Dialog>
</template>
